import React, { useState, useEffect } from "react";
import Logo from "../assets/images/rabta logo.png";
import win from "../assets/images/win.png";
import { toast } from "react-toastify";

import PuzzleGame from "../components/PuzzleGame";
import { getDeviceInfo, getBasicDeviceInfo } from "../utils/deviceInfo";

const pincodes = [
  193101, 193502, 193303, 193503, 193221, 193103, 193109, 193108, 193222, 193302, 193122, 193121,
  193223, 193501, 193201, 193224, 193505, 193123, 194104, 194103, 194401, 194106, 194107, 194301,
  194303, 194201, 192201, 192305, 192122, 192124, 192210, 192211, 193306, 192304, 192125, 192126,
  192301, 192221, 192129, 192303, 192401, 192123, 192212, 192232, 193411, 191121, 191113, 191201,
  191202, 190006, 193401, 192121, 190017, 191131, 193404, 193504, 194302, 194202, 192101, 191111,
  190009, 191112, 190010, 190014, 190003, 190015, 191132, 190099, 193301, 193225, 194101, 194102,
  194109, 192202, 190018, 191101, 193402, 192302, 190008, 191103, 190011, 190001, 190024, 190019,
  190004, 190002, 190021, 190005, 190007, 190025, 191102, 190012, 194105, 190020, 193403, 190023,
  192231,
];

const apiFieldToFormField = {
  fullName: "name",
  pincode: "pincode",
  age: "age",
  consumerCode: "uniqueCode",
  retailerCode: "retailerCode",
};

export default function Consumer() {
  const [step, setStep] = useState(1);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [errors, setErrors] = useState({});
  const [instructionStep, setInstructionStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consumerData, setConsumerData] = useState(null); // Store consumer response data
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  // Fetch device info on mount

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const info = await getDeviceInfo();
        setDeviceInfo(info);
      } catch (error) {
        console.error("Failed to get device info:", error);
      }
    };

    fetchInfo();
  }, []);

  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResendOtp(true);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    pincode: "",
    age: "",
    uniqueCode: "",
    retailerCode: "",
    smoker: "",
    terms: false,
  });

  // Separate state for mobile and OTP
  const [mobileData, setMobileData] = useState({
    mobile: "",
    otp: "",
  });

  useEffect(() => {
    localStorage.removeItem("consumer");
  }, []);

  // Check localStorage on component mount
  useEffect(() => {
    try {
      const storedConsumer = localStorage.getItem("consumer");

      if (storedConsumer) {
        const consumerData = JSON.parse(storedConsumer);

        // If consumer exists but NOT verified, clear localStorage and start fresh
        if (consumerData.isVerified != true) {
          localStorage.removeItem("consumer");
        } else {
          console.log("Verified consumer found in localStorage:", consumerData);

          // Consumer is verified, load their data
          setConsumerData(consumerData);
        }
      }
      // If no data exists, user naturally starts at step 1
    } catch (error) {
      // If there's an error parsing, clear the corrupted data
      localStorage.removeItem("consumer");
    }
  }, []);

  // Handle input for form data
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }

    // Restrict name to only letters and spaces
    if (name === "name") {
      if (value === "" || /^[A-Za-z\s]*$/.test(value)) {
        setFormData({ ...formData, [name]: value });
      }
      return;
    }

    // Restrict pincode to only numbers (max 6 digits)
    if (name === "pincode") {
      if (value === "" || (/^\d*$/.test(value) && value.length <= 6)) {
        setFormData({ ...formData, [name]: value });
      }
      return;
    }

    // Restrict age to only numbers (max 3 digits)
    if (name === "age") {
      if (value === "" || (/^\d*$/.test(value) && value.length <= 3)) {
        setFormData({ ...formData, [name]: value });
      }
      return;
    }

    // Restrict uniqueCode to alphanumeric only (max 7 characters)
    if (name === "uniqueCode") {
      if (value === "" || (/^[A-Za-z0-9]*$/.test(value) && value.length <= 7)) {
        setFormData({ ...formData, [name]: value.toUpperCase() });
      }
      return;
    }

    // Restrict retailerCode to only numbers (max 4 digits)
    if (name === "retailerCode") {
      if (value === "" || (/^\d*$/.test(value) && value.length <= 4)) {
        setFormData({ ...formData, [name]: value });
      }
      return;
    }

    // For other fields, handle normally
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  // Handle input for mobile data
  const handleMobileChange = (e) => {
    const { name, value } = e.target;

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }

    // Restrict mobile to only numbers
    if (name === "mobile") {
      if (value === "" || /^\d*$/.test(value)) {
        setMobileData({ ...mobileData, [name]: value });
      }
      return;
    }

    // Restrict OTP to only numbers
    if (name === "otp") {
      if (value === "" || /^\d*$/.test(value)) {
        setMobileData({ ...mobileData, [name]: value });
      }
      return;
    }

    setMobileData({ ...mobileData, [name]: value });
  };

  // --- FORM VALIDATION ---
  const validateForm = () => {
    let newErrors = {};

    // Full Name validation (at least 2 words, only letters/spaces)
    if (!formData.name) {
      newErrors.name = "Full name is required";
    } else if (!/^[A-Za-z]+(\s[A-Za-z]+)+$/.test(formData.name)) {
      newErrors.name = "Please enter your full name (first and last).";
    }

    // Pincode
    if (!formData.pincode) {
      newErrors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Pincode must be 6 digits";
    } else if (!pincodes.includes(parseInt(formData.pincode))) {
      newErrors.pincode = "Please enter a valid PIN code from Jammu & Kashmir.";
    }

    // Age validation (must be 18-120)
    if (!formData.age) {
      newErrors.age = "Age is required";
    } else if (!/^\d+$/.test(formData.age)) {
      newErrors.age = "Age must be a number";
    } else if (formData.age < 18 || formData.age > 120) {
      newErrors.age = "You need to be 18 or older to continue.";
    }

    // Unique Code - Must be exactly 7 alphanumeric characters
    if (!formData.uniqueCode) {
      newErrors.uniqueCode = "Unique Code is required";
    } else if (!/^[A-Za-z0-9]{7}$/.test(formData.uniqueCode)) {
      newErrors.uniqueCode = "Unique Code must be 7 characters";
    }

    // Retailer Code - Optional but if provided must be 4 digits
    if (formData.retailerCode && !/^\d{4}$/.test(formData.retailerCode)) {
      newErrors.retailerCode = "Retailer Code must be 4 digits";
    }

    // Smoker
    if (!formData.smoker) {
      newErrors.smoker = "Please select Yes or No";
    }

    // Terms
    if (!formData.terms) {
      newErrors.terms = "You must agree to the terms & privacy policy";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- API CALL TO REGISTER CONSUMER ---
  const registerConsumer = async () => {
    try {
      const deviceInfoPayload = deviceInfo
        ? {
            device: deviceInfo.device,
            os: deviceInfo.os,
            browser: deviceInfo.browser,
            ipAddress: deviceInfo.ipAddress,
            location: `${deviceInfo.location?.city}, ${deviceInfo.location?.region} ` || null,
            screenSize: deviceInfo.screen
              ? `${deviceInfo.screen?.width}x${deviceInfo.screen?.height}`
              : "",
            timezone: deviceInfo.timezone,
          }
        : {};

      const payload = {
        fullName: formData.name,
        age: parseInt(formData.age),
        pincode: parseInt(formData.pincode),
        consumerCode: formData.uniqueCode,
        ...(formData.retailerCode && { retailerCode: formData.retailerCode }),
        deviceInfo: deviceInfoPayload,
        termsAccepted: formData.terms,
      };

      // console.log({ payload });

      const response = await fetch(`${process.env.REACT_APP_API_URL}consumers/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        // If the API tells us which field is invalid, show the error under that field.
        if (data?.field) {
          const mapped = apiFieldToFormField[data.field] || data.field;
          setErrors((prev) => ({ ...prev, [mapped]: data.message || "Invalid value" }));
        } else {
          // Fallback to a general error only if no field was provided
          setErrors((prev) => ({ ...prev, submit: data.message || "Registration failed" }));
        }

        // optional toast
        toast.error(data.message || "Registration failed", {
          position: "top-center",
          autoClose: 3000,
        });

        return { success: false, error: data.message };
      }

      toast.success("Registration successful!", { position: "top-center", autoClose: 2000 });

      setFormData({
        name: "",
        age: "",
        pincode: "",
        uniqueCode: "",
        retailerCode: "",
        smoker: "",
        terms: false,
      });

      setConsumerData(data.data);
      return { success: true, data };
    } catch (error) {
      toast.error(error.message || "Something went wrong", {
        position: "top-center",
        autoClose: 3000,
      });
      // Keep this as a general error since it's a network/unexpected error
      setErrors((prev) => ({ ...prev, submit: error.message || "Something went wrong" }));
      return { success: false, error: error.message };
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit form → call API → then go to mobile screen or instructions
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    if (!validateForm()) return;

    // Set isSubmitting to true immediately to prevent duplicate clicks
    setIsSubmitting(true);

    // Clear any previous submit error before calling
    setErrors((prev) => ({ ...prev, submit: "" }));

    const result = await registerConsumer();

    if (result.success) {
      if (result.data?.data?.isVerified === true) {
        setStep(4);
      } else {
        setStep(2);
      }
    }
    // No else here: registerConsumer already set field-specific or submit error.
  };

  // --- API CALL TO SEND OTP ---
  const sendOtpApi = async (mobileNumber) => {
    try {
      setIsSubmitting(true);

      const payload = {
        mobileNumber: mobileNumber,
        consumerId: consumerData._id, // Get from stored consumer data
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}consumers/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send OTP");
      }

      // console.log("OTP sent successfully:", data);
      return { success: true, data };
    } catch (error) {
      console.error("Send OTP error:", error);
      return { success: false, error: error.message };
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- MOBILE VALIDATION & SEND OTP ---
  const handleSendOtp = async () => {
    let newErrors = {};
    if (!mobileData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(mobileData.mobile)) {
      newErrors.mobile = "Enter a valid 10-digit mobile number";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    // Call send OTP API
    const result = await sendOtpApi(mobileData.mobile);

    if (result.success) {
      // Start timer and move to OTP verification step
      setOtpTimer(60);
      setCanResendOtp(false);
      setStep(3);
      toast.success("OTP sent successfully!", { position: "top-center", autoClose: 2000 });
    } else {
      setErrors({
        mobile: result.error || "Failed to send OTP. Please try again.",
      });
    }
  };

  // --- RESEND OTP ---
  const handleResendOtp = async () => {
    if (!canResendOtp) return;

    const result = await sendOtpApi(mobileData.mobile);

    if (result.success) {
      setOtpTimer(60);
      setCanResendOtp(false);
      setMobileData((prev) => ({ ...prev, otp: "" }));
      toast.success("OTP resent successfully!", { position: "top-center", autoClose: 2000 });
    } else {
      toast.error(result.error || "Failed to resend OTP", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  // --- API CALL TO VERIFY OTP ---
  const verifyOtpApi = async (mobileNumber, otp) => {
    try {
      setIsSubmitting(true);

      const payload = {
        mobileNumber: mobileNumber,
        otp: otp,
        consumerId: consumerData._id, // Get from stored consumer data
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}consumers/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "OTP verification failed");
      }

      localStorage.setItem("consumer", JSON.stringify(data.data));
      setConsumerData(data.data);
      // console.log("OTP verified successfully:", data);
      return { success: true, data };
    } catch (error) {
      console.error("Verify OTP error:", error);
      return { success: false, error: error.message };
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- OTP VERIFICATION ---
  const handleVerifyOtp = async () => {
    if (!mobileData.otp.trim()) {
      setErrors({ otp: "Enter OTP" });
      return;
    }

    setErrors({});

    // Call verify OTP API
    const result = await verifyOtpApi(mobileData.mobile, mobileData.otp);

    if (result.success) {
      // Show "Verified" message
      setIsOtpVerified(true);

      // Wait 2 seconds before moving to next step
      setTimeout(() => {
        setIsOtpVerified(false);
        setStep(4); // go to instructions
      }, 2000); // 2 second delay
    } else {
      setErrors({
        otp: result.error || "Invalid OTP. Please try again.",
      });
    }
  };

  const handleStepTrack = async (stepName) => {
    try {
      // 1️⃣ Get consumer object from localStorage
      const consumer = JSON.parse(localStorage.getItem("consumer"));

      if (!consumer || !consumer._id) {
        console.error("❌ Consumer data or ID not found in localStorage");
        return;
      }

      const _id = consumer._id;

      // 2️⃣ Make PUT request to update step
      const response = await fetch(`${process.env.REACT_APP_API_URL}consumers/update/${_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          step: stepName,
        }),
      });

      const data = await response.json();

      // 3️⃣ Handle API response
      if (!response.ok) {
        console.error("❌ Failed to update step:", data.message || data);
        return;
      }

      console.log("✅ Step updated successfully:", data);
    } catch (error) {
      console.error("❌ Error while updating step:", error);
    }
  };

  // --- Instructions ---
  const handleInstructionNext = () => {
    if (instructionStep < 3) {
      setInstructionStep(instructionStep + 1);
    } else {
      handleStepTrack("Instructions Screen");
      setStep(5); // after last step → Play Now
    }
  };

  // --- Play Now ---
  const handlePlayNow = () => {
    setStep(6);
    handleStepTrack("Play Now Screen");
  };

  const instructions = [
    {
      title: "Honeydew Blend",
      text: "A selection of the finest \n Virginia tobacco \n from around the world.",
    },
    {
      title: "Impeccable taste",
      text: (
        <span className="circle-text">
          <span className="fifty-six">56</span>
          <span className="circle-right">
            {" "}
            Hours <br />
            of sun.
          </span>
        </span>
      ),
    },
    {
      title: "Firm filter",
      text: "A firm feel filter from \n start to finish",
    },
  ];

  return (
    <div className="main-bg " style={{ width: "100%" }}>
      {showThankYouModal ? (
        <div>
          <div
            style={{
              borderRadius: "12px",
              padding: "24px",
              width: "100%",
              color: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="test">
              <div>
                <div
                  style={{
                    marginBottom: "20px",
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "40px",
                  }}
                >
                  <img src={Logo} alt="logo main" style={{ maxWidth: 240 }} />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "22px",
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "#0b6535",
                      textShadow: "yellow 1px 1px",
                      fontWeight: "bold",
                      fontSize: "30px",
                      marginBottom: 10,
                    }}
                  >
                    THANK YOU!
                  </h3>
                </div>

                <p
                  style={{
                    marginBottom: "20px",
                    lineHeight: "1.6",
                    fontSize: "16px",
                    textAlign: "center",
                  }}
                >
                  Thanks for your participation during the campaign. Stay tuned as we announce the
                  winners of mega rewards.
                </p>
              </div>
              <p style={{ textAlign: "center", marginTop: "300px", fontSize: "14px" }}>
                For any queries, you may contact us through 0120-4420155 or drop us a mail on
                raabta@blinkdigital.in
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* STEP 1: FORM */}
          {step === 1 && (
            <div className="home-wrapper">
              <img src={Logo} alt="logo main" />
              <form onSubmit={handleFormSubmit} className="form-card">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name*"
                  value={formData?.name || ""}
                  onChange={handleChange}
                />
                {errors.name && <p className="error">{errors.name}</p>}

                <input
                  type="text"
                  name="pincode"
                  placeholder="Pincode*"
                  value={formData?.pincode || ""}
                  onChange={handleChange}
                />
                {errors.pincode && <p className="error">{errors.pincode}</p>}

                <input
                  type="number"
                  name="age"
                  placeholder="Age*"
                  value={formData?.age || ""}
                  onChange={handleChange}
                />
                {errors.age && <p className="error">{errors.age}</p>}

                <div className="input-wrapper">
                  <input
                    type="text"
                    name="uniqueCode"
                    placeholder="Unique Code*"
                    value={formData?.uniqueCode || ""}
                    onChange={handleChange}
                    maxLength="7"
                  />
                  <div className="tooltip-container">
                    i
                    <div className="tooltip-content">
                      Enter the Unique Code from the pack insert here. Each pack has its own Unique
                      Code, and each code can only be used once to play the game.
                    </div>
                  </div>
                </div>
                {errors.uniqueCode && <p className="error">{errors.uniqueCode}</p>}
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="retailerCode"
                    placeholder="Retailer Code"
                    value={formData?.retailerCode || ""}
                    onChange={handleChange}
                    maxLength="4"
                  />
                  <div className="tooltip-container">
                    i
                    <div className="tooltip-content">
                      Please ask the retailer you purchased from for their Retailer Code and enter
                      it here so they can also be rewarded.
                    </div>
                  </div>
                </div>
                {errors.retailerCode && <p className="error">{errors.retailerCode}</p>}

                <div className="radio-group">
                  <label className="radio-label">Do you smoke?</label>
                  <label>
                    <input
                      type="radio"
                      name="smoker"
                      value="yes"
                      checked={formData?.smoker === "yes" || false}
                      onChange={handleChange}
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="smoker"
                      value="no"
                      checked={formData?.smoker === "no" || false}
                      onChange={handleChange}
                    />
                    No
                  </label>
                </div>
                {errors.smoker && <p className="error">{errors.smoker}</p>}

                <div className="checkbox-group">
                  <label className="flex-label">
                    <span className="custom-checkbox-wrapper">
                      <input
                        type="checkbox"
                        name="terms"
                        checked={formData?.terms || false}
                        onChange={handleChange}
                      />
                      <span className="custom-checkbox"></span>
                    </span>
                    <span className="checkbox-text">
                      I hereby state that I am older than 18 years of age and agree to the{" "}
                      <a className="tnc" href="/terms-conditions" target="_blank" rel="noreferrer">
                        Terms & Conditions
                      </a>{" "}
                      and{" "}
                      <a className="tnc" href="/privacy-policy" target="_blank" rel="noreferrer">
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>
                </div>
                {errors.terms && <p className="error">{errors.terms}</p>}

                {/* Show general API error if any */}
                {errors.submit && <p className="error">{errors.submit}</p>}

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={formData?.smoker !== "yes" || !formData?.terms || isSubmitting}
                >
                  {isSubmitting ? "PLEASE WAIT..." : "GET STARTED"}
                </button>
              </form>
            </div>
          )}

          {/* STEP 2: MOBILE */}
          {step === 2 && (
            <div className="flex-common">
              <img className="logo-main" src={Logo} alt="logo main" />
              <div className="otp-block">
                <h2>Enter Mobile Number</h2>
                <input
                  type="text"
                  name="mobile"
                  placeholder="Enter 10-digit mobile number"
                  value={mobileData.mobile}
                  onChange={handleMobileChange}
                  maxLength="10"
                />
                {errors.mobile && <p className="error">{errors.mobile}</p>}
                <button onClick={handleSendOtp} disabled={isSubmitting}>
                  {isSubmitting ? "SENDING..." : "Send OTP"}
                </button>
                <button
                  className="back-btn"
                  onClick={() => {
                    // Clear localStorage when going back to step 1
                    localStorage.removeItem("consumer");
                    setConsumerData(null);
                    setStep(1);
                    setErrors({});
                  }}
                  disabled={isSubmitting}
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: OTP */}
          {step === 3 && (
            <div className="flex-common">
              <img className="logo-main" src={Logo} alt="logo main" />
              <div className="otp-block">
                <h2>Verify your number</h2>
                <input
                  type="text"
                  name="otp"
                  placeholder="Enter 6-digit OTP"
                  value={mobileData.otp}
                  onChange={handleMobileChange}
                  maxLength="6"
                />

                {/* Show success message only when timer is active */}
                {otpTimer > 0 && <p>OTP sent successfully to your mobile number</p>}

                {errors.otp && <p className="error">{errors.otp}</p>}

                {/* Show Verify OTP button only when timer is active */}
                {otpTimer > 0 && (
                  <button
                    onClick={handleVerifyOtp}
                    disabled={isSubmitting || isOtpVerified}
                    className={isOtpVerified ? "submit-btn verified" : ""}
                    style={{ marginTop: "20px" }}
                  >
                    {isSubmitting ? "Verifying..." : isOtpVerified ? "✓ Verified" : "Confirm"}
                  </button>
                )}

                {/* Resend OTP Section */}
                <div className="resend-otp-section">
                  {otpTimer > 0 ? (
                    <p className="timer-text">
                      Resend OTP in <span className="timer-count">{otpTimer}s</span>
                    </p>
                  ) : (
                    <button
                      className="resend-btn btn-common"
                      onClick={handleResendOtp}
                      disabled={isSubmitting || !canResendOtp}
                    >
                      {isSubmitting ? "RESENDING..." : "Resend OTP"}
                    </button>
                  )}
                </div>

                <button
                  className="back-btn"
                  onClick={() => {
                    setStep(2);
                    setMobileData((prev) => ({ ...prev, otp: "" }));
                    setErrors({});
                    setIsOtpVerified(false);
                    setOtpTimer(0);
                    setCanResendOtp(true);
                  }}
                  disabled={isSubmitting || isOtpVerified}
                >
                  Go Back
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: INSTRUCTIONS */}
          {step === 4 && (
            <div className="instructions-wrapper">
              <div className="wrapper">
                <div>
                  <img className="logo-main" src={Logo} alt="logo main" />
                </div>
                <div>
                  <h2>{instructions[instructionStep - 1].title}</h2>
                  <p style={{ whiteSpace: "pre-line" }}>{instructions[instructionStep - 1].text}</p>
                </div>
                <button className="btn-next" onClick={handleInstructionNext}>
                  {instructionStep < instructions.length ? "Next ➤" : "Next ➤"}
                </button>
              </div>
            </div>
          )}

          {/* STEP 5: PLAY NOW */}
          {step === 5 && (
            <div className="win-wrapper">
              <h2>Ready to Play!</h2>
              <p>Complete the Raabta themed puzzle & stand a chance to win exciting rewards</p>
              <img className="win-img" src={win} alt="win imag" />
              <button onClick={handlePlayNow}>Play Now</button>
            </div>
          )}

          {/* STEP 6: GAME PAGE */}
          {step === 6 && <PuzzleGame />}

          {step == 1 ? (
            <div className="footer-line">
              <div>
                Got a question or need support?
                <br /> Reach us at <strong>0120-4420155</strong> between{" "}
                <strong>10:00 AM and 5:30 PM (Monday–Saturday).</strong>
              </div>
              <div className="bottom-footer-line">
                Contest developed and managed by{" "}
                <strong>
                  <a
                    href="https://blinkdigital.in/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "inherit",
                      textDecoration: "none",
                    }}
                  >
                    Blink Digital
                  </a>
                </strong>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
